import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Button from '@material-ui/core/Button'
import { makeStyles, Theme } from '@material-ui/core/styles'
import clsx from 'clsx'
import React from 'react'
import { PartnershipType, ProductListingDialogFacet } from '.'
import { DialogContext } from './DialogContext'
import { DialogFilterContext } from './DialogFilterContext'
import { DialogFilter } from './Header/DialogFilter.old'

const useStyles = makeStyles((theme: Theme) => ({
    header: {
        margin: 'unset',
        textAlign: 'left',
        width: '100%',
        height: pxToRem(47),
        color: theme.palette.common.white,
        fontSize: pxToRem(23),
        background: '#A78966',
    },
    headerText: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        textTransform: 'uppercase',
        paddingLeft: theme.spacing(3),
    },
    productHeader: {
        padding: theme.spacing(3),
        paddingBottom: theme.spacing(2),
    },
    actionButton: {
        borderRadius: 100,
        color: '#A78966',
        padding: theme.spacing(1.25),
        margin: theme.spacing(0, 1.25, 0, 1.25),
        height: pxToRem(36),
        fontSize: pxToRem(14),
        fontWeight: 'normal',
    },
    actionButtonFill: {
        color: theme.palette.common.white,
        background: '#A78966',
        '&:hover': {
            background: '#8F795F',
        },
    },
}))

interface AdvancedFiltersProps {
    listingType: string[]
    listingTypeOptions: string[]
    handleListingType: (value: string[]) => void
    product: string
    productOptions: string[]
    handleProduct: (value: string) => void
    handleAutocompleteNameChange: (facet?: ProductListingDialogFacet) => void
    autosuggestLoading?: boolean
    bimResources?: string[]
    handlePartnershipSelection?: (
        type: PartnershipType,
        value: string[]
    ) => void
    sustainabilityResources?: string[]
    sustainabilityResourcesOptions?: string[]
    bimResourcesOptions?: string[]
    queryParams?: URLSearchParams
    selectedTab: number
}

export const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({
    listingType,
    listingTypeOptions,
    handleListingType,
    product,
    productOptions,
    handleProduct,
    handleAutocompleteNameChange,
    autosuggestLoading,
    bimResources,
    sustainabilityResources,
    handlePartnershipSelection,
    sustainabilityResourcesOptions,
    bimResourcesOptions,
    queryParams,
    selectedTab,
}) => {
    const classes = useStyles()

    const nodeId = queryParams?.get('nodeId')

    const { handleSlideOpen, handleAdvancedFiltersOpen } = React.useContext(
        DialogContext
    )
    const { getFacetSelectedOptions } = React.useContext(DialogContext)
    const { handleFacetSelectedChange, getFacetOptions } = React.useContext(
        DialogFilterContext
    )

    const handleClose = React.useCallback(() => {
        handleAutocompleteNameChange()
    }, [handleAutocompleteNameChange])

    const getValue = React.useCallback(
        (value: ProductListingDialogFacet) => {
            return getFacetSelectedOptions(value)
        },
        [getFacetSelectedOptions]
    )

    const getOption = React.useCallback(
        (value: ProductListingDialogFacet) => {
            return getFacetOptions(value)
        },
        [getFacetOptions]
    )

    const handleFilterCategoriesValue = React.useCallback(
        (facet: ProductListingDialogFacet, value: string[]) => {
            value && handleFacetSelectedChange(facet, value as string[])
        },
        [handleFacetSelectedChange]
    )

    const handleListingLevelFilter = React.useCallback(
        (value: string | string[] | null) => {
            handleListingType(value as string[])
        },
        [handleListingType]
    )

    const handleClearFilters = React.useCallback(() => {
        handlePartnershipSelection?.('BIM Resources', [] as string[])
        handlePartnershipSelection?.('Sustainability Resources', [] as string[])
        handleListingLevelFilter([] as string[])
        if (nodeId) {
            handleProduct('All Listings' as string)
        } else {
            handleFilterCategoriesValue('Categories', [] as string[])
        }
    }, [
        handlePartnershipSelection,
        handleListingLevelFilter,
        handleProduct,
        handleFilterCategoriesValue,
    ])

    const handleCloseSlideOut = React.useCallback(() => {
        handleAdvancedFiltersOpen?.(false)
        handleSlideOpen?.(false)
    }, [handleAdvancedFiltersOpen, handleSlideOpen])

    return (
        <>
            <GridContainer className={classes.header}>
                <GridItem
                    data-testid="advancedFiltersHeading"
                    className={clsx(classes.headerText)}
                >
                    Advanced Filters
                </GridItem>
            </GridContainer>
            {nodeId ? (
                <GridContainer
                    direction="row"
                    className={classes.productHeader}
                >
                    <GridItem sm={12}>
                        <DialogFilter
                            name="product"
                            value={product}
                            options={productOptions}
                            handleValue={(value) =>
                                handleProduct(value as string)
                            }
                            type="single"
                        />
                    </GridItem>
                </GridContainer>
            ) : (
                <GridContainer
                    direction="row"
                    className={classes.productHeader}
                >
                    <GridItem sm={12}>
                        <DialogFilter
                            name="category"
                            label="Categories"
                            value={getValue('Categories')}
                            options={getOption('Categories')}
                            handleValue={(value) =>
                                handleFilterCategoriesValue(
                                    'Categories',
                                    value as string[]
                                )
                            }
                            type="multiple"
                        />
                    </GridItem>
                </GridContainer>
            )}
            <GridContainer direction="row" className={classes.productHeader}>
                <GridItem sm={12}>
                    <DialogFilter
                        name="sustainabilityResources"
                        label="Sustainability Resources"
                        value={sustainabilityResources!}
                        options={sustainabilityResourcesOptions ?? []}
                        handleValue={(value) =>
                            handlePartnershipSelection?.(
                                'Sustainability Resources',
                                value as string[]
                            )
                        }
                        handleClose={handleClose}
                        loading={autosuggestLoading}
                        type="multiple"
                        fullWidth={true}
                    />
                </GridItem>
            </GridContainer>
            <GridContainer direction="row" className={classes.productHeader}>
                <GridItem sm={12}>
                    <DialogFilter
                        name="bimResources"
                        label="BIM Resources"
                        value={bimResources!}
                        options={bimResourcesOptions ?? []}
                        handleValue={(value) =>
                            handlePartnershipSelection?.(
                                'BIM Resources',
                                value as string[]
                            )
                        }
                        handleClose={handleClose}
                        loading={autosuggestLoading}
                        type="multiple"
                        fullWidth={true}
                    />
                </GridItem>
            </GridContainer>
            <GridContainer direction="row" className={classes.productHeader}>
                <GridItem sm={12}>
                    <DialogFilter
                        name="listingType"
                        label="Listing Type"
                        value={listingType}
                        options={listingTypeOptions}
                        handleValue={handleListingLevelFilter}
                        type="multiple"
                        fullWidth={true}
                    />
                </GridItem>
            </GridContainer>
            <GridContainer
                direction="row"
                alignItems="center"
                justifyContent="space-around"
            >
                <GridItem>
                    <Button
                        onClick={handleClearFilters}
                        className={classes.actionButton}
                        data-testid="clearFiltersButton"
                    >
                        Clear All
                    </Button>
                    <Button
                        onClick={handleCloseSlideOut}
                        className={clsx(
                            classes.actionButton,
                            classes.actionButtonFill
                        )}
                        data-testid="closeButton"
                    >
                        Close
                    </Button>
                </GridItem>
            </GridContainer>
        </>
    )
}
